<template>
    <div class="demo-data" :class="rootClass" :style="rootStyle">
        <div
            v-if="!overlay && backroundLink"
            class="blur-bacground"
            :style="{
                background: 'url(' + backroundLink + ')',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                filter: 'blur(1px)',
                width: '100%',
                height: computedHeight + 'px',
                position: 'absolute',
            }"
        ></div>
        <section class="lock-card">
            <header class="lock-card__head">
                <h2>
                    <template v-if="titleTail">
                        {{ titleHead }}
                        <span class="lock-card__nowrap">{{ titleTail }}</span>
                    </template>
                    <template v-else>{{ title }}</template>
                </h2>
            </header>
            <div class="lock-card__body">
                <div class="lock-notice">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" stroke-width="2" />
                        <path
                            d="M8 10V7a4 4 0 0 1 8 0v3"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                        />
                    </svg>
                    <div>
                        <strong>Доступ к проекту приостановлен</strong>
                        <ul class="lock-facts">
                            <li v-if="blockDateLabel">Дата блокировки: {{ blockDateLabel }}</li>
                            <li v-if="daysUntilChannelBlockText">{{ daysUntilChannelBlockText }}</li>
                            <li v-if="activeChannelsText">{{ activeChannelsText }}</li>
                        </ul>
                        <p>Оплатите проект, {{ noticeGoal }}</p>
                    </div>
                </div>
                <p class="lock-card__helper">
                    После подтверждения оплаты доступ к проекту восстановится автоматически.
                </p>
            </div>
            <footer class="lock-card__footer">
                <button class="lock-btn lock-btn--secondary" type="button" @click="redirectSubscription">
                    Перейти в подписку
                </button>
                <button class="lock-btn lock-btn--primary" type="button" @click="redirectBilling">
                    Оплатить проект
                </button>
            </footer>
        </section>
    </div>
</template>

<script>
import { useProject } from '@root/src/hooks/useProject'
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import useRouter from '@/hooks/useRouter'
import { useBillingStore } from '@/store/stores/billing'
import { ensureProjectBillingLoaded } from '@/helpers/channels/ensureProjectBillingLoaded'
import api from '@/api/api'
import { getSlots } from '@/api/networkFixer'
import {
    fetchLiveAuthStatus,
    getAuthStatusConnectorId,
    isChannelAuthorized,
    shouldUseLiveAuthStatus,
} from '@/helpers/channels/channelAuthStatus'
import { getChannelDisplayName, getChannelIndex } from '@/helpers/channels/channelTableDisplay'
import { CHANNEL_TYPES } from '@/vars/general'
import moment from 'moment'

const CHANNELS_GRACE_DAYS = 3
const CHANNEL_DISCONNECT_GOAL = 'чтобы каналы не отключились.'
const NO_CHANNELS_GOAL = 'чтобы продолжить пользоваться системой.'

export default {
    name: 'DemoSubView',
    props: {
        backroundLink: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: 'Проект заблокирован из-за неоплаты',
        },
        /** Overlay over real blurred content (e.g. Multichat) — no fake background image */
        overlay: {
            type: Boolean,
            default: false,
        },
        /** Keep the card in view while scrolling a tall locked section */
        sticky: {
            type: Boolean,
            default: false,
        },
        noticeGoal: {
            type: String,
            default: 'чтобы каналы не отключились.',
        },
    },
    setup(props) {
        const computedHeight = ref(400)
        const { project } = useProject()
        const root = getCurrentInstance()?.proxy
        const billingStore = useBillingStore()

        if (!props.overlay && props.backroundLink) {
            const linkImage = new Image()
            linkImage.src = props.backroundLink
            linkImage.onload = () => {
                computedHeight.value = linkImage.height
            }
        }

        if (project.value?.id && project.value?.billingEnabled) {
            ensureProjectBillingLoaded(project.value.id, project.value)
        }

        const currentTariff = computed(() => billingStore.getCurrentTariff(project.value?.id))
        const previousTariff = computed(() => billingStore.getPreviousTariff(project.value?.id))

        const blockDate = computed(() => {
            if (currentTariff.value) return null
            const raw = previousTariff.value?.uninstalledAt
            if (!raw) return null
            const date = moment(raw)
            return date.isValid() ? date : null
        })

        const channelSlots = ref([])
        const authStatusByConnectorId = ref({})
        const authStatusLoadingByConnectorId = ref({})

        const loadChannelSlots = async () => {
            const projectId = project.value?.id
            if (!projectId) {
                channelSlots.value = []
                return
            }
            const { data, error } = await getSlots({ projectId })
            channelSlots.value = !error && Array.isArray(data) ? data : []
        }

        onMounted(loadChannelSlots)
        watch(() => project.value?.id, loadChannelSlots)

        const blockDateLabel = computed(() =>
            blockDate.value ? blockDate.value.format('DD.MM.YYYY') : ''
        )

        const daysUntilChannelBlock = computed(() => {
            if (!blockDate.value) return null
            const deadline = blockDate.value.clone().add(CHANNELS_GRACE_DAYS, 'days').startOf('day')
            return deadline.diff(moment().startOf('day'), 'days')
        })

        const occupiedChannels = computed(() => {
            const connectors = project.value?.connectors || []
            return channelSlots.value
                .map(slot => {
                    if (!slot?.channelConnectorId) return null
                    return connectors.find(
                        connector =>
                            connector?.connectorId === slot.channelConnectorId &&
                            Object.values(CHANNEL_TYPES).includes(connector.connectorType) &&
                            connector.channelId &&
                            !connector.disabled
                    )
                })
                .filter(Boolean)
        })

        const asAuthItem = connector => ({ ...connector, isActive: true })

        const refreshAuthStatuses = () => {
            occupiedChannels.value.forEach(connector => {
                const authItem = asAuthItem(connector)
                if (!shouldUseLiveAuthStatus(authItem)) return
                fetchLiveAuthStatus(authItem, {
                    apiClient: api,
                    hasStatus: id => authStatusByConnectorId.value[id] !== undefined,
                    isLoading: id => Boolean(authStatusLoadingByConnectorId.value[id]),
                    setLoading: (id, value) => {
                        authStatusLoadingByConnectorId.value = {
                            ...authStatusLoadingByConnectorId.value,
                            [id]: value,
                        }
                    },
                    setStatus: (id, value) => {
                        authStatusByConnectorId.value = {
                            ...authStatusByConnectorId.value,
                            [id]: value,
                        }
                    },
                })
            })
        }

        watch(
            () => occupiedChannels.value.map(connector => getAuthStatusConnectorId(connector)).join(','),
            refreshAuthStatuses,
            { immediate: true }
        )

        const activeChannelNames = computed(() => {
            const connectors = project.value?.connectors || []
            const authorized = occupiedChannels.value.filter(connector => {
                const authItem = asAuthItem(connector)
                if (shouldUseLiveAuthStatus(authItem)) {
                    const connectorId = getAuthStatusConnectorId(authItem)
                    if (
                        !connectorId ||
                        authStatusLoadingByConnectorId.value[connectorId] ||
                        authStatusByConnectorId.value[connectorId] === undefined
                    ) {
                        return false
                    }
                }
                return !connector.disabled && isChannelAuthorized(authItem, {
                    statusByConnectorId: authStatusByConnectorId.value,
                    loadingByConnectorId: authStatusLoadingByConnectorId.value,
                })
            })

            return authorized.map(connector => {
                const providerType = connector.providerType || connector.connectorType
                const index = getChannelIndex(providerType, connector, connectors)
                return getChannelDisplayName(providerType, index)
            })
        })

        const channelAuthPending = computed(() =>
            occupiedChannels.value.some(connector => {
                const authItem = asAuthItem(connector)
                if (!shouldUseLiveAuthStatus(authItem)) return false
                const connectorId = getAuthStatusConnectorId(authItem)
                return (
                    !connectorId ||
                    authStatusLoadingByConnectorId.value[connectorId] ||
                    authStatusByConnectorId.value[connectorId] === undefined
                )
            })
        )

        const noConnectedChannels = computed(
            () => !channelAuthPending.value && activeChannelNames.value.length === 0
        )

        const daysUntilChannelBlockText = computed(() => {
            const days = daysUntilChannelBlock.value
            if (days === null) return ''
            if (noConnectedChannels.value && days >= 0) return ''
            if (days < 0) return 'Срок отправки с каналов уже истёк'
            if (days === 0) return 'Каналы отключатся сегодня'
            const mod100 = days % 100
            const mod10 = days % 10
            let word = 'дней'
            let verb = 'осталось'
            if (mod100 < 11 || mod100 > 14) {
                if (mod10 === 1) {
                    word = 'день'
                    verb = 'остался'
                } else if (mod10 >= 2 && mod10 <= 4) {
                    word = 'дня'
                }
            }
            return `До блокировки каналов ${verb} ${days} ${word}`
        })

        const activeChannelsText = computed(() => {
            if (channelAuthPending.value) return ''

            const names = activeChannelNames.value
            if (!names.length) return 'Сейчас нет подключённых каналов, с которых идут отправки.'
            if (names.length === 1) return `Сообщения отправляются через ${names[0]}.`
            if (names.length === 2) return `Сообщения отправляются через ${names[0]} и ${names[1]}.`
            return `Сообщения отправляются через ${names.slice(0, -1).join(', ')} и ${names[names.length - 1]}.`
        })

        const goToBilling = section => {
            if (!project.value?.id) return
            useRouter(root).router.push(`/project/${project.value.id}/billing?section=${section}`)
        }

        const redirectBilling = () => goToBilling('tariffs')
        const redirectSubscription = () => goToBilling('subscriptions')

        return {
            redirectBilling,
            redirectSubscription,
            computedHeight,
            blockDateLabel,
            daysUntilChannelBlockText,
            activeChannelsText,
            noticeGoal: computed(() =>
                noConnectedChannels.value && props.noticeGoal === CHANNEL_DISCONNECT_GOAL
                    ? NO_CHANNELS_GOAL
                    : props.noticeGoal
            ),
        }
    },
    computed: {
        rootClass() {
            return {
                'demo-data--overlay': this.overlay,
                'demo-data--sticky': this.overlay && this.sticky,
            }
        },
        titleTail() {
            const marker = 'из-за неоплаты'
            if (!this.title || this.title.indexOf(marker) === -1) return ''
            return marker
        },
        titleHead() {
            if (!this.titleTail) return this.title
            return this.title.replace(this.titleTail, '').trim()
        },
        rootStyle() {
            if (this.overlay) {
                return {
                    width: '100%',
                    height: '100%',
                }
            }
            return {
                width: '100%',
                backgroundSize: 'cover',
                height: this.computedHeight + 'px',
            }
        },
    },
}
</script>

<style lang="sass" scoped>
.demo-data
    display: flex
    align-items: center
    justify-content: center
    position: relative
    padding: 24px
    box-sizing: border-box
    &--overlay
        position: absolute
        inset: 0
        z-index: 50
        width: 100%
        height: 100%
        padding: clamp(16px, 4vw, 32px)
        background: transparent
        pointer-events: none
    &--sticky
        align-items: flex-start
        .lock-card
            position: sticky
            top: clamp(64px, 10vh, 96px)

.lock-card
    --indigo: #525ad5
    --indigo-hover: #454dc4
    --indigo-tint: #eef0ff
    --ink: #1a1a2e
    --line: rgba(26, 26, 46, .1)
    --text-2: rgba(26, 26, 46, .68)
    --red: #e8183c
    --red-tint: #ffe8ed
    position: relative
    z-index: 1
    display: flex
    flex-direction: column
    width: min(560px, 100%)
    max-width: 100%
    overflow: hidden
    border: 1px solid var(--line)
    border-radius: 24px
    background: #fff
    box-shadow: 0 24px 72px rgba(26, 26, 46, .22)
    color: var(--ink)
    isolation: isolate
    transform: translateZ(0)
    pointer-events: auto

.lock-card__head
    padding: 20px 24px 16px
    border-bottom: 1px solid var(--line)
    h2
        margin: 0
        font-size: 20px
        font-weight: 800
        line-height: 1.25
        letter-spacing: -.02em

.lock-card__nowrap
    white-space: nowrap

.lock-card__body
    display: flex
    flex-direction: column
    gap: 16px
    padding: 24px

.lock-notice
    display: grid
    grid-template-columns: 24px minmax(0, 1fr)
    align-items: start
    gap: 12px
    padding: 16px
    border: 1px solid rgba(232, 24, 60, .28)
    border-radius: 16px
    background: var(--red-tint)
    svg
        width: 22px
        height: 22px
        color: var(--red)
    strong
        display: block
        margin-bottom: 4px
        font-size: 14px
        font-weight: 700
        line-height: 1.35
    p
        margin: 8px 0 0
        color: var(--text-2)
        font-size: 13px
        line-height: 1.55

.lock-facts
    margin: 8px 0 0
    padding: 0
    list-style: none
    li
        margin: 0
        color: var(--text-2)
        font-size: 13px
        line-height: 1.55
        & + li
            margin-top: 4px

.lock-card__helper
    margin: 0
    color: var(--text-2)
    font-size: 12px
    line-height: 1.5

.lock-card__footer
    display: flex
    flex-wrap: wrap
    justify-content: flex-end
    gap: 12px
    padding: 16px 24px
    border-top: 1px solid var(--line)
    background: #fff

.lock-btn
    min-height: 44px
    display: inline-flex
    align-items: center
    justify-content: center
    padding: 0 16px
    border: 1px solid transparent
    border-radius: 12px
    font-size: 14px
    font-weight: 700
    cursor: pointer
    transition: background .14s ease, border-color .14s ease, transform .14s ease, box-shadow .14s ease
    &:hover
        transform: translateY(-1px)
    &:active
        transform: translateY(0)
    &--primary
        background: var(--indigo)
        color: #fff
        box-shadow: 0 8px 22px rgba(82, 90, 213, .22)
        &:hover
            background: var(--indigo-hover)
    &--secondary
        background: #fff
        color: var(--indigo)
        border-color: rgba(82, 90, 213, .3)
        &:hover
            background: var(--indigo-tint)

@media (max-width: 600px)
    .demo-data--overlay .lock-card
        max-height: calc(100vh - 120px)
        overflow: auto
        overscroll-behavior: contain
    .lock-card
        border-radius: 20px
    .lock-card__head
        padding: 16px 16px 12px
        h2
            font-size: 18px
    .lock-card__head,
    .lock-card__body
        padding-left: 16px
        padding-right: 16px
    .lock-card__footer
        flex-direction: column
        padding: 12px 16px
        .lock-btn
            width: 100%
</style>
